// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useNavigate } from "@tanstack/react-router";
import type { ColumnDef, PaginationState, SortingState } from "@tanstack/react-table";
import { Filter, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/api/client";
import { useStoresQuery } from "@/api/hooks/use-stores";
import { DataTable } from "@/components/data-table";
import { StorePlusIcon } from "@/components/icons";
import { FilterSelect } from "@/components/shared/filter-select";
import { FilterToggle } from "@/components/shared/filter-toggle";
import { KeyChip } from "@/components/shared/key-chip";
import { RowId } from "@/components/shared/row-id";
import { StatusIndicator } from "@/components/shared/status-indicator";
import { StoreLink } from "@/components/shared/store-link";
import { TimezoneCell } from "@/components/shared/timezone-cell";
import { Button } from "@/components/ui/button";
import { CompareStoreDialog } from "@/features/stores/compare-stores-dialog";
import { CreateStoreDialog } from "@/features/stores/create-store-dialog";
import { StoreActionsMenu } from "@/features/stores/store-actions-menu";
import { ToggleStoreActiveDialog } from "@/features/stores/toggle-store-active-dialog";
import { useBackofficeContext } from "@/hooks/use-backoffice-context";
import { useEscapeToClearFilters } from "@/hooks/use-escape-to-clear-filters";
import { downloadCsv } from "@/lib/csv";
import { decodeFilters, encodeFilters } from "@/lib/filter-params";
import { getDefaultPageSize } from "@/lib/preferences";

interface ChainStoresTabProps {
  chainName: string;
  initialFilters?: string;
}

interface StoreRow {
  storeId: number;
  storeName: string;
  legibleName: string;
  storeCode?: string;
  timeZone: string;
  regionName?: string;
  isActive: boolean;
}

type StoresSortBy = "storeId" | "storeName" | "legibleName" | "regionName" | "timeZone";

const FILTER_DEFAULTS = {
  search: "",
  region: "",
  onlyActive: true,
  page: 0,
  get pageSize() {
    return getDefaultPageSize();
  },
  sortId: "legibleName",
  sortDesc: false,
};

export function ChainStoresTab({ chainName, initialFilters }: ChainStoresTabProps) {
  const { t } = useTranslation("back-office");
  const navigate = useNavigate();
  const { data: context, isLoading: contextLoading } = useBackofficeContext();
  const initial = useMemo(() => decodeFilters(initialFilters, FILTER_DEFAULTS), [initialFilters]);

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: initial.page,
    pageSize: initial.pageSize,
  });
  const [sorting, setSorting] = useState<SortingState>([{ id: initial.sortId, desc: initial.sortDesc }]);
  const [search, setSearch] = useState(initial.search);
  const [regionFilter, setRegionFilter] = useState(initial.region);
  const [onlyActive, setOnlyActive] = useState(initial.onlyActive);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [duplicateFrom, setDuplicateFrom] = useState<{ copyFrom: string; chainName: string; timeZone: string }>();
  const [compareDialogStore, setCompareDialogStore] = useState<string | null>(null);
  const [toggleStore, setToggleStore] = useState<StoreRow | null>(null);

  const availableRegions = useMemo(
    () => context?.regionsByChain?.[chainName] ?? [],
    [chainName, context?.regionsByChain],
  );

  const isFiltered = !!(search || regionFilter || !onlyActive);

  const syncUrl = useCallback(
    (overrides: Partial<typeof FILTER_DEFAULTS> = {}) => {
      const current = {
        search,
        region: regionFilter,
        onlyActive,
        page: pagination.pageIndex,
        pageSize: pagination.pageSize,
        sortId: sorting[0]?.id ?? "legibleName",
        sortDesc: sorting[0]?.desc ?? false,
        ...overrides,
      };
      const encoded = encodeFilters(current, FILTER_DEFAULTS);
      navigate({
        to: "/chains/$chainName",
        params: { chainName },
        search: { t: "stores", ...(encoded ? { f: encoded } : {}) },
        replace: true,
      });
    },
    [search, regionFilter, onlyActive, pagination.pageIndex, pagination.pageSize, sorting, navigate, chainName],
  );

  useEffect(
    function synchronizeFiltersToUrl() {
      syncUrl();
    },
    [syncUrl],
  );

  const clearFilters = useCallback(function clearFilters() {
    setSearch("");
    setRegionFilter("");
    setOnlyActive(true);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  useEscapeToClearFilters({
    isFiltered,
    clearFilters,
    disabled: createDialogOpen || compareDialogStore !== null || toggleStore !== null,
  });

  const sortBy = (sorting[0]?.id ?? "legibleName") as StoresSortBy;
  const orderBy = sorting[0]?.desc ? ("desc" as const) : ("asc" as const);

  const { data, isLoading, refetch, isFetching } = useStoresQuery({
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    search: search || undefined,
    sortBy,
    orderBy,
    chainName,
    regionName: regionFilter || undefined,
    onlyActive: onlyActive || undefined,
  });

  const handleExport = useCallback(async () => {
    const res = await api.GET("/v1/stores", {
      params: {
        query: {
          page: 1,
          limit: -1,
          search: search || undefined,
          sortBy,
          orderBy,
          "filter[chainName]": chainName,
          "filter[regionName]": regionFilter || undefined,
          "filter[onlyActive]": onlyActive || undefined,
        },
      },
    });
    const result = res.data as unknown as {
      stores: Array<{
        storeId: number;
        storeName: string;
        legibleName: string;
        timeZone: string;
        regionName?: string;
        isActive: boolean;
      }>;
    };
    downloadCsv(
      `chain-${chainName}-stores.csv`,
      ["ID", "Name", "Key", "Region", "Timezone", "Status"],
      result.stores.map((s) => [
        String(s.storeId),
        s.legibleName,
        s.storeName,
        s.regionName ?? "",
        s.timeZone,
        s.isActive ? "Active" : "Inactive",
      ]),
    );
  }, [chainName, search, sortBy, orderBy, regionFilter, onlyActive]);

  const columns = useMemo<ColumnDef<StoreRow, unknown>[]>(
    () => [
      {
        accessorKey: "storeId",
        header: t("stores.list.columns.id"),
        size: 80,
        enableHiding: false,
        meta: { columnLabel: t("stores.list.columns.id"), className: "pl-4", forceWidth: true, sticky: "left" },
        cell: ({ row }) => <RowId id={row.original.storeId} />,
      },
      {
        accessorKey: "legibleName",
        header: t("stores.list.columns.legibleName"),
        meta: { columnLabel: t("stores.list.columns.legibleName") },
        cell: ({ row }) => (
          <StoreLink
            storeName={row.original.storeName}
            legibleName={row.original.legibleName}
            className="font-medium"
          />
        ),
      },
      {
        accessorKey: "storeName",
        header: t("stores.list.columns.storeName"),
        meta: { columnLabel: t("stores.list.columns.storeName") },
        cell: ({ row }) => <KeyChip label={row.original.storeName} variant="store" />,
      },
      {
        accessorKey: "storeCode",
        header: t("stores.list.columns.storeCode"),
        enableSorting: false,
        meta: { columnLabel: t("stores.list.columns.storeCode"), defaultHidden: true },
        cell: ({ row }) => row.original.storeCode ?? "—",
      },
      {
        accessorKey: "regionName",
        header: t("stores.list.columns.regionName"),
        meta: { columnLabel: t("stores.list.columns.regionName") },
        cell: ({ row }) => row.original.regionName ?? "—",
      },
      {
        accessorKey: "timeZone",
        header: t("stores.list.columns.timezone"),
        meta: { columnLabel: t("stores.list.columns.timezone") },
        cell: ({ row }) => <TimezoneCell timeZone={row.original.timeZone} />,
      },
      {
        id: "isActive",
        header: t("stores.list.columns.status"),
        size: 80,
        enableSorting: false,
        meta: { columnLabel: t("stores.list.columns.status") },
        cell: ({ row }) => <StatusIndicator active={row.original.isActive} />,
      },
      {
        id: "actions",
        size: 50,
        meta: { forceWidth: true, sticky: "right" },
        enableSorting: false,
        enableHiding: false,
        cell: ({ row }) => (
          <StoreActionsMenu
            isActive={row.original.isActive}
            onDuplicate={() => {
              setDuplicateFrom({
                copyFrom: row.original.storeName,
                chainName,
                timeZone: row.original.timeZone,
              });
              setCreateDialogOpen(true);
            }}
            onCompareSettings={() => setCompareDialogStore(row.original.storeName)}
            onToggleActive={() => setToggleStore(row.original)}
          />
        ),
      },
    ],
    [t, chainName],
  );

  return (
    <div className="p-6">
      <DataTable
        storageKey="chain-stores"
        columns={columns}
        data={(data?.stores ?? []) as StoreRow[]}
        totalCount={data?.count ?? 0}
        isLoading={isLoading}
        pagination={pagination}
        onPaginationChange={setPagination}
        sorting={sorting}
        onSortingChange={setSorting}
        search={search}
        onSearchChange={setSearch}
        searchDebounceMs={1000}
        onRefresh={refetch}
        isRefreshing={isFetching}
        onExport={handleExport}
        searchPlaceholder={t("stores.list.searchPlaceholder", "Search stores...")}
        toolbar={
          <Button
            onClick={() => {
              setDuplicateFrom(undefined);
              setCreateDialogOpen(true);
            }}
          >
            <StorePlusIcon className="h-4 w-4" />
            {t("stores.list.addStoreButton")}
          </Button>
        }
        filters={
          <>
            {availableRegions.length > 0 && (
              <FilterSelect
                label={t("stores.list.filter.region")}
                value={regionFilter}
                onChange={(v) => {
                  setRegionFilter(v);
                  setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                }}
                options={availableRegions.map((r) => ({ label: r, value: r }))}
                searchPlaceholder={t("stores.list.filter.searchRegions")}
                emptyText={t("stores.list.filter.noRegions")}
                loading={contextLoading}
              />
            )}
            <FilterToggle
              active={onlyActive}
              onChange={() => {
                setOnlyActive((prev) => !prev);
                setPagination((prev) => ({ ...prev, pageIndex: 0 }));
              }}
              icon={Filter}
              label={t("common.active")}
            />
            {isFiltered && (
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9 text-muted-foreground hover:text-foreground"
                onClick={clearFilters}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </>
        }
      />

      <CreateStoreDialog
        key={duplicateFrom?.copyFrom ?? "create"}
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        duplicateFrom={duplicateFrom}
        defaultChainName={chainName}
      />

      <CompareStoreDialog
        defaultStoreName={compareDialogStore ?? ""}
        open={compareDialogStore != null}
        onOpenChange={(open) => {
          if (!open) setCompareDialogStore(null);
        }}
      />

      <ToggleStoreActiveDialog store={toggleStore} onClose={() => setToggleStore(null)} />
    </div>
  );
}
